import { Link } from "react-router-dom";
import { ArrowRight, Bot, Sparkles } from "lucide-react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { AIAgentSettingsCard } from "../components/ai/AIAgentSettingsCard";
import { McpConnectionsCard } from "../components/ai/McpConnectionsCard";

/**
 * Pagina unica di configurazione AI.
 * Spec: docs/ai-program/10-byok-and-mcp-rework.md
 *
 * Due modi di usare l'AI, entrambi a carico del coach:
 *
 *  - **connettore MCP**: il proprio abbonamento Claude o ChatGPT, nessun costo
 *    aggiuntivo, si lavora dal client invece che da qui;
 *  - **assistente in-app**: chiave API a consumo, si paga per token.
 *
 * L'ordine non e' casuale e non e' estetico. Il connettore e' la strada che
 * consigliamo: usa un abbonamento che il coach quasi sempre ha gia', invece di
 * una chiave API a consumo che va aperta, ricaricata e sorvegliata. Metterlo
 * primo, con la guida dentro, e' il modo di dirlo senza togliere l'altra
 * strada a chi la preferisce.
 *
 * Prende il posto della vecchia pagina "AI Coach", che era un guscio con
 * endpoint sospesi dalla Fase 0.
 */
export default function AISettings() {
	const { user } = useAuth();
	const isCoach = user?.role === "coach" || user?.is_admin;

	return (
		<Layout>
			<div className="max-w-3xl mx-auto p-4 md:p-6">
				<div className="mb-6">
					<h1 className="font-heading text-2xl md:text-3xl flex items-center gap-2">
						<Bot className="w-6 h-6 text-volt" />
						Assistente AI
					</h1>
					<p className="text-muted-foreground">
						Due modi di usarlo, entrambi con il tuo account. Ti consigliamo il
						connettore: lavori nel Claude o nel ChatGPT che usi già, e il
						programma te lo trovi scritto qui dentro senza ricopiare niente.
					</p>
				</div>

				{!isCoach ? (
					<p className="text-sm text-muted-foreground">
						L'assistente AI è riservato agli account coach.
					</p>
				) : (
					<div className="space-y-6">
						<Link
							to="/ai-guide"
							className="flex items-center gap-3 rounded-sm border border-volt/40 bg-volt/5 p-3 hover:bg-volt/10"
						>
							<Sparkles className="h-5 w-5 shrink-0 text-volt" />
							<span className="min-w-0 flex-1">
								<span className="block text-sm font-medium">
									Cosa puoi chiedere all'assistente
								</span>
								<span className="block text-xs text-muted-foreground">
									Programmi, import ed export, analisi, monitoraggio, gare e
									modelli prestativi: esempi pronti da copiare.
								</span>
							</span>
							<ArrowRight className="h-4 w-4 shrink-0 text-volt" />
						</Link>

						<McpConnectionsCard />

						<div className="space-y-2">
							<p className="text-xs uppercase tracking-wide text-muted-foreground">
								Oppure, se preferisci restare qui dentro
							</p>
							<p className="text-sm text-muted-foreground">
								La chat dentro l'app fa le stesse cose, ma va a consumo sulla
								tua chiave API: la apri tu sul sito del fornitore e paghi i
								token che usi. È l'alternativa per chi non ha un abbonamento
								Claude o ChatGPT.
							</p>
						</div>
						<AIAgentSettingsCard />
					</div>
				)}
			</div>
		</Layout>
	);
}
